import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    CircularProgress,
    Alert,
    IconButton,
    Chip,
} from "@mui/material";
import CustomDropdown from "./CustomDropdown";
import { SecondaryButton, PrimaryButton } from "./Buttons";
import {
    Close as CloseIcon,
    CloudUpload as CloudUploadIcon,
    Description as DescriptionIcon,
} from "@mui/icons-material";
import { getDocumentTypes, uploadDocument } from "../../services/documents";
import { MAX_DOCUMENT_UPLOAD_MB } from "../../constants/documentUpload";
import { validateFile, formatFileSize } from "../../utils/documentUtils";
import { CustomTextField } from "./CustomTextFields";
import { toast } from "react-toastify";

const DocumentUploadDialog = ({ 
    open, 
    onClose, 
    referenceType, 
    referenceId, 
    documentType = null, // If null, show document type selector
    title = "Upload Document",
    onSuccess,
    allowedFileTypes = ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "png", "jpeg"],
    maxSizeMB = MAX_DOCUMENT_UPLOAD_MB
}) => {
    const [file, setFile] = useState(null);
    const [documentTypeId, setDocumentTypeId] = useState('');
    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [documentTypes, setDocumentTypes] = useState([]);

    useEffect(() => {
        if (open) {
            fetchDocumentTypes();
        }
    }, [open]);

    // Set documentTypeId when documentType prop changes
    useEffect(() => {
        if (documentType && documentType.id) {
            setDocumentTypeId(documentType.id);
        }
    }, [documentType]);

    // Auto-select document type ONLY when documentType prop is provided
    useEffect(() => {
        // Only auto-select if documentType prop is provided
        if (documentType && documentType.id && documentTypes.length > 0) {
            setDocumentTypeId(documentType.id);
        }
    }, [documentTypes, referenceType, documentType]);

    const fetchDocumentTypes = async () => {
        try {
            const types = await getDocumentTypes();
            
            // Filter document types based on reference type for better UX
            let filteredTypes = types;
            if (referenceType) {
                // For service orders, prioritize invoice, quotation, work order
                if (referenceType === 'service_order') {
                    const priorityTypes = ['invoice', 'quotation', 'work_order'];
                    filteredTypes = types.sort((a, b) => {
                        const aPriority = priorityTypes.indexOf(a.name.toLowerCase());
                        const bPriority = priorityTypes.indexOf(b.name.toLowerCase());
                        if (aPriority === -1 && bPriority === -1) return 0;
                        if (aPriority === -1) return 1;
                        if (bPriority === -1) return -1;
                        return aPriority - bPriority;
                    });
                }
                // For users, prioritize credentials, certification, id_document
                else if (referenceType === 'user') {
                    const priorityTypes = ['credentials', 'certification', 'id_document'];
                    filteredTypes = types.sort((a, b) => {
                        const aPriority = priorityTypes.indexOf(a.name.toLowerCase());
                        const bPriority = priorityTypes.indexOf(b.name.toLowerCase());
                        if (aPriority === -1 && bPriority === -1) return 0;
                        if (aPriority === -1) return 1;
                        if (bPriority === -1) return -1;
                        return aPriority - bPriority;
                    });
                }
                // For equipment, prioritize manual, specification, warranty
                else if (referenceType === 'equipment') {
                    const priorityTypes = ['manual', 'specification', 'warranty'];
                    filteredTypes = types.sort((a, b) => {
                        const aPriority = priorityTypes.indexOf(a.name.toLowerCase());
                        const bPriority = priorityTypes.indexOf(b.name.toLowerCase());
                        if (aPriority === -1 && bPriority === -1) return 0;
                        if (aPriority === -1) return 1;
                        if (bPriority === -1) return -1;
                        return aPriority - bPriority;
                    });
                }
                // For consumables, prioritize specification, safety_data, technical
                else if (referenceType === 'consumable') {
                    const priorityTypes = ['specification', 'safety_data', 'technical'];
                    filteredTypes = types.sort((a, b) => {
                        const aPriority = priorityTypes.indexOf(a.name.toLowerCase());
                        const bPriority = priorityTypes.indexOf(b.name.toLowerCase());
                        if (aPriority === -1 && bPriority === -1) return 0;
                        if (aPriority === -1) return 1;
                        if (bPriority === -1) return -1;
                        return aPriority - bPriority;
                    });
                }
            }

            setDocumentTypes(filteredTypes);

            // Auto-select document type if provided
            if (documentType && documentType.id) {
                const foundType = filteredTypes.find(type => type.id === documentType.id);
                if (foundType) {
                    setDocumentTypeId(foundType.id);
                }
            }
            // Auto-select default type if no documentType prop and types available
            else if (filteredTypes.length > 0) {
                const defaultType = filteredTypes[0];
                setDocumentTypeId(defaultType.id);
            }
        } catch (error) {
            console.error('Error fetching document types:', error);
            setError('Failed to load document types');
            toast.error('Failed to load document types');
        }
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setError("");
        }
    };

    const handleSubmit = async () => {
        if (!file || !documentTypeId) {
            setError("Please select a file and document type");
            return;
        }

        // Validate file
        const validation = validateFile(file, allowedFileTypes, maxSizeMB);
        if (!validation.isValid) {
            setError(validation.error);
            return;
        }

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            const uploadData = {
                file: file,
                documentTypeId: documentTypeId,
                description: description.trim() || "",
                referenceType: referenceType,
                referenceId: referenceId.toString()
            };

            const response = await uploadDocument(uploadData);
            
            setSuccess("Document uploaded successfully!");
            setFile(null);
            setDescription("");
            
            // Call onSuccess callback if provided
            if (onSuccess) {
                onSuccess(response);
            }
            
            // Close dialog after a short delay
            setTimeout(() => {
                handleClose();
            }, 1500);
            
        } catch (error) {
            // Extract error message from response (check message, detail, error field, then DRF errors dict)
            console.error('Upload error response:', error.response?.data);
            const data = error.response?.data;
            let msg = null;
            if (data?.errors && typeof data.errors === 'object') {
                const parts = Object.entries(data.errors).map(([key, val]) => {
                    const text = Array.isArray(val) ? val.join(', ') : String(val);
                    return `${key}: ${text}`;
                });
                if (parts.length > 0) msg = parts.join(' ');
            }
            msg = msg
                || data?.message
                || (typeof data?.detail === 'string' ? data.detail : null)
                || data?.error
                || error.message
                || "Failed to upload document";
            console.log('Final error message:', msg);
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Reset form state
        setFile(null);
        setDescription("");
        setError("");
        setSuccess("");
        
        // Reset documentTypeId based on whether documentType prop is provided
        if (!documentType) {
            setDocumentTypeId('');
        } else {
            setDocumentTypeId(documentType.id);
        }
        
        onClose();
    };

    const getFileSize = (bytes) => {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    const isFileValid = (file) => {
        if (!file) return false;
        const validation = validateFile(file, allowedFileTypes, maxSizeMB);
        return validation.isValid;
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6" component="div">
                        {title}
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {referenceType.charAt(0).toUpperCase() + referenceType.slice(1)} ID: {referenceId}
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                {/* Document Type Selection - Always show when no documentType prop is provided */}
                {!documentType ? (
                    <Box sx={{ mb: 3 }}>
                        <CustomDropdown
                            label="Document type"
                            value={documentTypeId || ''}
                            onChange={(e) => setDocumentTypeId(e.target.value)}
                            options={documentTypes}
                            getOptionValue={(opt) => opt.id}
                            getOptionLabel={(opt) => opt.name}
                            emptyLabel="Select a document type"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {referenceType === 'service_order' && 'Choose invoice for billing, quotation for estimates, or work order for job details'}
                            {referenceType === 'user' && 'Choose credentials for qualifications, certification for professional licenses, or ID documents'}
                            {referenceType === 'equipment' && 'Choose manual for user guides, specification for technical details, or warranty for coverage'}
                            {referenceType === 'consumable' && 'Choose specification for technical details, safety data for MSDS, or technical for other specs'}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                        <Typography variant="body2" color="info.main">
                            <strong>Document Type:</strong> {documentType.name}
                        </Typography>
                    </Box>
                )}

                {/* Description Field */}
                <CustomTextField
                    fullWidth
                    label="Description (Optional)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    multiline
                    rows={2}
                    sx={{ mb: 3 }}
                />

                {/* File Upload */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Allowed types: {allowedFileTypes.join(", ")} | Max size: {maxSizeMB}MB
                    </Typography>
                    <input
                        accept={allowedFileTypes.map(type => `.${type}`).join(',')}
                        style={{ display: "none" }}
                        id="document-file-input"
                        type="file"
                        onChange={handleFileChange}
                    />
                    <label htmlFor="document-file-input">
                        <SecondaryButton
                            component="span"
                            size="small"
                            startIcon={<CloudUploadIcon />}
                            fullWidth
                            sx={{
                                py: 2,
                                borderStyle: "dashed",
                                borderWidth: 2,
                                "&:hover": { borderWidth: 2 },
                            }}
                        >
                            {file ? "Change file" : "Select file"}
                        </SecondaryButton>
                    </label>
                </Box>

                {/* File Preview */}
                {file && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                            <DescriptionIcon color="primary" />
                            <Typography variant="subtitle2" component="span">
                                {file.name}
                            </Typography>
                        </Box>
                        <Box display="flex" gap={1} flexWrap="wrap">
                            <Chip
                                label={getFileSize(file.size)}
                                size="small"
                                variant="outlined"
                            />
                            <Chip
                                label={file.name.split(".").pop().toUpperCase()}
                                size="small"
                                variant="outlined"
                            />
                            {!isFileValid(file) && (
                                <Chip
                                    label="Invalid file"
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Allowed types: {allowedFileTypes.join(", ")} | Max size: {maxSizeMB}MB
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
                <PrimaryButton
                    onClick={handleSubmit}
                    disabled={!file || !documentTypeId || !isFileValid(file) || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                >
                    {loading ? "Uploading…" : "Upload document"}
                </PrimaryButton>
            </DialogActions>
        </Dialog>
    );
};

export default DocumentUploadDialog; 